import React, { useEffect } from 'react';
import {
  TextField,
  MenuItem,
  InputAdornment,
  Box,
  Typography,
} from '@mui/material';
import { COUNTRIES_CODE } from '../../constants/constants';
import { getClientHeaders } from '../../services/clientInfo';


// Get real flag image via country code (using countryflagsapi)
const getFlagUrl = (countryCode) =>
  `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;

const CountryPhoneInput = ({ data, updateData }) => {
  // Support both legacy (data.countryCode holds dial code) and new (data.countryIso holds ISO) shapes.
  const selectedCountry =
    COUNTRIES_CODE.find((c) => c.code === data.countryIso) ||
    COUNTRIES_CODE.find((c) => c.dial_code === data.countryCode) ||
    COUNTRIES_CODE[0];

  // Auto-detect user's location and set country code on mount
  useEffect(() => {
    // Only auto-detect if no country is already set
    if (!data.countryIso && !data.countryCode) {
      const detectLocation = async () => {
        try {
          const clientHeaders = await getClientHeaders();
          const detectedCountryCode = clientHeaders['X-Client-Country-Code'];
          
          if (detectedCountryCode) {
            const detectedCountry = COUNTRIES_CODE.find(
              (c) => c.code === detectedCountryCode
            );
            
            if (detectedCountry) {
              updateData({
                ...data,
                countryIso: detectedCountry.code,
                countryCode: detectedCountry.dial_code,
              });
            }
          }
        } catch (error) {
          console.warn('Failed to detect location for country code', error);
          // Silently fail - user can manually select
        }
      };
      
      detectLocation();
    }
  }, []); // Empty dependency array - only run on mount

  const handleCountryChange = (e) => {
    const selected = COUNTRIES_CODE.find((c) => c.code === e.target.value);
    if (!selected) return;
    updateData({
      ...data,
      countryIso: selected.code,
      countryCode: selected.dial_code, // keep dial code for backward compatibility
      phoneNumber: '', // reset phone number when changing country
    });
  };

  const handlePhoneChange = (e) => {
    const raw = e.target.value.replace(/\s+/g, '');
    // Store only local digits; dial code is handled separately
    const normalizedLocal = raw.replace(/[^0-9]/g, '');
    updateData({
      ...data,
      phoneNumber: normalizedLocal,
      phoneNumberCountryCode: selectedCountry.code,
      phoneNumberDialCode: selectedCountry.dial_code,
    });
  };

  return (
    <TextField
      required
      fullWidth
      variant="outlined"
      size="medium"
      label="Phone Number"
      value={data.phoneNumber || ''}
      onChange={handlePhoneChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <TextField
              select
              value={data.countryIso || selectedCountry.code}
              onChange={handleCountryChange}
              variant="standard"
              sx={{
                minWidth: 90,
                background: 'transparent',
                '.MuiInputBase-root:before': { borderBottom: 'none' },
                '.MuiInputBase-root:after': { borderBottom: 'none' },
                '.MuiInputBase-input': {
                  display: 'flex',
                  alignItems: 'center',
                  padding: 0,
                },
              }}
            >
              {COUNTRIES_CODE.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <img
                      src={getFlagUrl(country.code)}
                      alt={country.code}
                      width="20"
                      height="14"
                      style={{ borderRadius: '2px' }}
                    />
                    <Typography variant="body2">{country.dial_code}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </InputAdornment>
        ),
      }}
    />
  );
};

export default CountryPhoneInput;
